import React, { useState, useEffect, useContext } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { UserContext } from "../../../../../UserContext.js";
import { progressService } from "../../../../../services/progressService";
import pandaAvatar from "../../../../../assets/PandaProfile.png";

const StakeholderRelationshipRadar = ({
  userId,
  subsectionId,
  data,
  onPreviousStep,
  onNextStep,
}) => {
  const { user: contextUser } = useContext(UserContext);
  const userName = contextUser?.firstName || "You";

  const [visibilityMap, setVisibilityMap] = useState(data?.visibilityMap || {});
  const [userProfileImage, setUserProfileImage] = useState(null);
  const [loading, setLoading] = useState(false);

  /* ---------------- DATA LOAD ---------------- */

  useEffect(() => {
    async function loadProfile() {
      try {
        const res = await progressService.getUserProgress(
          userId,
          "f90552a1-12fd-4f0b-b4f5-17e258b84b97"
        );
        if (res?.data?.payload?.profileImage) {
          setUserProfileImage(res.data.payload.profileImage);
        }
      } catch {}
    }
    loadProfile();
  }, [userId]);

  useEffect(() => {
    async function loadRadar() {
      try {
        setLoading(true);
        const res = await progressService.getUserProgress(userId, subsectionId);
        if (res?.data?.visibilityMap) {
          setVisibilityMap(res.data.visibilityMap);
        }
      } finally {
        setLoading(false);
      }
    }
    loadRadar();
  }, [userId, subsectionId]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        Loading radar...
      </div>
    );
  }

  /* ---------------- RADAR CONSTANTS ---------------- */

  const CANVAS_WIDTH = 1440;
  const CANVAS_HEIGHT = 1080;

  const CENTER_X = 150;   // sun pushed left
  const CENTER_Y = 420;

  const START_RADIUS = 300;
  const GAP = 280;
  const ORBITS = [0, 1, 2, 3].map(i => START_RADIUS + i * GAP);

  /* ---------------- PER-ORBIT CONFIG ---------------- */
  const ORBIT_CONFIG = {
    q3: { baseAngle: 10, arc: 30, leftShift: 40 },  // innermost
    q4: { baseAngle: 345, arc: 35, leftShift: 40 },
    q2: { baseAngle: 350, arc: 40, leftShift: 40 },
    q1: { baseAngle: 0, arc: 45, leftShift: 25 },  // outermost
  };

  const polarToCartesian = (r, deg) => {
    const rad = (deg * Math.PI) / 180;
    return { x: r * Math.cos(rad), y: r * Math.sin(rad) };
  };

  const getStakeholders = key => visibilityMap[key]?.items || [];

  /* ---------------- RENDER ---------------- */

  return (
    <div className="relative w-full h-[90vh] bg-white overflow-hidden">

      {/* ================= HEADER ================= */}
      <div className="absolute top-8 left-1/2 -translate-x-1/2 z-50 text-center">
        <h3 className="text-[32px] font-medium text-[#323232]">
          Nice work - {userName}! Based on your inputs, here's how
          <br />
          distant you are from your stakeholders
        </h3>
      </div>

      {/* ================= RADAR VIEWPORT ================= */}
      <div
        className="absolute left-0 right-0"
        style={{
          overflowY: "hidden",
          overflowX: "hidden",
        }}
      >
        {/* ================= RADAR CANVAS ================= */}
        <div
          className="relative mx-auto"
          style={{
            width: `${CANVAS_WIDTH}px`,
            height: `${CANVAS_HEIGHT}px`,
            minWidth: `${CANVAS_WIDTH}px`,
            minHeight: `${CANVAS_HEIGHT}px`,
          }}
        >

          {/* -------- ORBITS -------- */}
          {ORBITS.map(r => (
            <div
              key={r}
              className="absolute rounded-full border border-[#5EB1BF]"
              style={{
                width: r * 2,
                height: r * 2,
                left: CENTER_X - r,
                top: CENTER_Y - r,
              }}
            />
          ))}

          {/* -------- SUN (USER) -------- */}
          <div
            className="absolute z-30"
            style={{
              left: CENTER_X,
              top: CENTER_Y,
              transform: "translate(-50%, -50%)",
            }}
          >
            <div className="w-[176px] h-[176px] rounded-full bg-[#E7E7E7] shadow-md">
              <img
                src={userProfileImage || pandaAvatar}
                alt={userName}
                className="w-[141px] h-[141px] object-cover rounded-full absolute inset-0 m-auto"
              />
            </div>
          </div>

          {/* -------- STAKEHOLDERS -------- */}
          {["q3", "q4", "q2", "q1"].flatMap((qKey, orbitIndex) => {
            const items = getStakeholders(qKey);
            if (!items.length) return [];

            const { baseAngle, arc, leftShift } = ORBIT_CONFIG[qKey];
            const step = arc / (items.length + 1);

            return items.map((s, i) => {
              const angle =
                items.length === 1
                  ? baseAngle
                  : baseAngle - arc / 2 + step * (i + 1);

              const { x, y } = polarToCartesian(ORBITS[orbitIndex], angle);

              return (
                <div
                  key={s.id || `${qKey}-${i}`}
                  className="absolute text-center"
                  style={{
                    left: CENTER_X + x + leftShift,
                    top: CENTER_Y - y,
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <div className="w-16 h-16 rounded-full bg-white border-2 border-[#FF5722] shadow-md flex items-center justify-center">
                    {s.animal ? (
                      <img src={s.animal} className="w-10 h-10" alt="s.animal" />
                    ) : (
                      "🐾"
                    )}
                  </div>
                  <p className="mt-2 text-sm font-medium">{s.name}</p>
                  <p className="text-xs">{s.relation}</p>
                </div>
              );
            });
          })}

        </div>
      </div>

      {/* ================= CHEVRONS ================= */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-50 flex gap-3">
        <button
          onClick={onPreviousStep}
          className="w-10 h-10 rounded-full bg-[#F5F5F5] shadow flex items-center justify-center"
        >
          <ChevronLeft />
        </button>
        <button
          onClick={onNextStep}
          className="w-10 h-10 rounded-full bg-[#5EB1BF] text-white shadow flex items-center justify-center"
        >
          <ChevronRight />
        </button>
      </div>

    </div>
  );
};

export default StakeholderRelationshipRadar;


// import React, { useState, useEffect, useContext } from "react";
// import { motion } from "framer-motion";
// import { ChevronLeft, ChevronRight } from "lucide-react";
// import { UserContext } from "../../../../../UserContext.js";
// import { progressService } from "../../../../../services/progressService";
// import pandaAvatar from "../../../../../assets/PandaProfile.png";

// const StakeholderRelationshipRadar = ({
//   userId,
//   subsectionId,
//   data,
//   onPreviousStep,
//   onNextStep,
// }) => {
//   const { user: contextUser } = useContext(UserContext);
//   const userName = contextUser?.firstName || "You";

//   const [visibilityMap, setVisibilityMap] = useState(data?.visibilityMap || {});
//   const [userProfileImage, setUserProfileImage] = useState(null);
//   const [loading, setLoading] = useState(false);

//   /* ---------------- DATA LOAD ---------------- */

//   useEffect(() => {
//     async function loadProfile() {
//       try {
//         const res = await progressService.getUserProgress(
//           userId,
//           "f90552a1-12fd-4f0b-b4f5-17e258b84b97"
//         );
//         if (res?.data?.payload?.profileImage) {
//           setUserProfileImage(res.data.payload.profileImage);
//         }
//       } catch {}
//     }
//     loadProfile();
//   }, [userId]);

//   useEffect(() => {
//     async function loadRadar() {
//       try {
//         setLoading(true);
//         const res = await progressService.getUserProgress(userId, subsectionId);
//         if (res?.data?.visibilityMap) {
//           setVisibilityMap(res.data.visibilityMap);
//         }
//       } finally {
//         setLoading(false);
//       }
//     }
//     loadRadar();
//   }, [userId, subsectionId]);

//   if (loading) {
//     return (
//       <div className="h-screen flex items-center justify-center">
//         Loading radar...
//       </div>
//     );
//   }

//   /* ---------------- RADAR CONSTANTS ---------------- */

//   const CANVAS_WIDTH = 1440;
//   const CANVAS_HEIGHT = 1080;

//   const CENTER_X = 150;   // sun pushed left
//   const CENTER_Y = 420;

//   const START_RADIUS = 300;
//   const GAP = 280;
//   const ORBITS = [0, 1, 2, 3].map(i => START_RADIUS + i * GAP);

//   const polarToCartesian = (r, deg) => {
//     const rad = (deg * Math.PI) / 180;
//     return { x: r * Math.cos(rad), y: r * Math.sin(rad) };
//   };

//   const getStakeholders = key => visibilityMap[key]?.items || [];

//   /* ---------------- RENDER ---------------- */

//   return (
//     <div className="relative w-full h-[90vh] bg-white overflow-hidden">

//       {/* ================= HEADER ================= */}
//       <div className="absolute top-8 left-1/2 -translate-x-1/2 z-50 text-center">
//         <h3 className="text-[32px] font-medium text-[#323232]">
//           Nice work - {userName}! Based on your inputs, here's how
//           <br />
//           distant you are from your stakeholders
//         </h3>
//       </div>

//       {/* ================= RADAR VIEWPORT (HEIGHT LOCKED) ================= */}
//       <div
//         className="absolute left-0 right-0"
//         style={{
           
//           overflowY: "hidden",
//           overflowX: "hidden",
//         }}
//       >

//         {/* ================= RADAR CANVAS ================= */}
//         <div
//           className="relative mx-auto"
//           style={{
//             width: `${CANVAS_WIDTH}px`,
//             height: `${CANVAS_HEIGHT}px`,
//             minWidth: `${CANVAS_WIDTH}px`,
//             minHeight: `${CANVAS_HEIGHT}px`,
//           }}
//         >

//           {/* -------- ORBITS -------- */}
//           {ORBITS.map(r => (
//             <div
//               key={r}
//               className="absolute rounded-full border border-[#5EB1BF]"
//               style={{
//                 width: r * 2,
//                 height: r * 2,
//                 left: CENTER_X - r,
//                 top: CENTER_Y - r,
//               }}
//             />
//           ))}

//           {/* -------- SUN (USER) -------- */}
//           <div
//             className="absolute z-30"
//             style={{
//               left: CENTER_X,
//               top: CENTER_Y,
//               transform: "translate(-50%, -50%)",
//             }}
//           >
//             <div className="w-[176px] h-[176px] rounded-full bg-[#E7E7E7] shadow-md">
//               <img
//                 src={userProfileImage || pandaAvatar}
//                 alt={userName}
//                 className="w-[141px] h-[141px] object-cover rounded-full absolute inset-0 m-auto"
//               />
//             </div>
//           </div>

//           {/* -------- STAKEHOLDERS -------- */}
//           {["q3", "q4", "q2", "q1"].flatMap((qKey, orbitIndex) => {
//             const items = getStakeholders(qKey);
//             if (!items.length) return [];

//             return items.map((s, i) => {
//               const angle = items.length === 1
//                 ? 0
//                 : -20 + (40 / (items.length + 1)) * (i + 1);

//               const { x, y } = polarToCartesian(ORBITS[orbitIndex], angle);

//               return (
//                 <div
//                   key={s.id || `${qKey}-${i}`}
//                   className="absolute text-center"
//                   style={{
//                     left: CENTER_X + x,
//                     top: CENTER_Y - y,
//                     transform: "translate(-50%, -50%)",
//                   }}
//                 >
//                   <div className="w-16 h-16 rounded-full bg-white border-2 border-[#FF5722] shadow-md flex items-center justify-center">
//                     {s.animal
//                       ? <img src={s.animal} className="w-10 h-10" />
//                       : "🐾"}
//                   </div>
//                   <p className="mt-2 text-sm font-medium">{s.name}</p>
//                   <p className="text-xs">{s.relation}</p>
//                 </div>
//               );
//             });
//           })}

//         </div>
//       </div>

//       {/* ================= CHEVRONS ================= */}
//       <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-50 flex gap-3">
//         <button
//           onClick={onPreviousStep}
//           className="w-10 h-10 rounded-full bg-[#F5F5F5] shadow flex items-center justify-center"
//         >
//           <ChevronLeft />
//         </button>
//         <button
//           onClick={onNextStep}
//           className="w-10 h-10 rounded-full bg-[#5EB1BF] text-white shadow flex items-center justify-center"
//         >
//           <ChevronRight />
//         </button>
//       </div>

//     </div>
//   );
// };

// export default StakeholderRelationshipRadar;
